<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import { genericForms } from '@shared/mixins/forms';

export default {
  mixins: [genericForms],
  form: {
    target_audience: {
      label: 'Public cible *',
      type: 'text',
      column: 'is-12',
      errors: ['required', { name: 'minLength', args: [5] }, { name: 'maxLength', args: [500] }],
      inputAttrs: {
        required: true,
        maxlength: 500,
        placeholder: 'entrepreneurs débutants, salariés en reconversion, managers d\'équipe, étudiants en marketing…',
      },
    },
    training_goal: {
      label: 'Objectif de la formation *',
      type: 'text',
      column: 'is-12',
      errors: ['required', { name: 'minLength', args: [5] }, { name: 'maxLength', args: [500] }],
      inputAttrs: {
        required: true,
        maxlength: 500,
        placeholder: 'comprendre les bases du marketing digital, apprendre les gestes de premiers secours, être autonome pour trouver ses premiers clients.',
      },
    },
    prompt: {
      label: 'Description de votre formation *',
      type: 'textarea',
      column: 'is-12',
      errors: ['required', { name: 'minLength', args: [10] }, { name: 'maxLength', args: [2000] }],
      inputAttrs: {
        required: true,
        maxlength: 2000,
        placeholder: '- un résumé de la formation\n- des points clés de la formation\n- les objectifs, le public cible',
      },
    },
  },
  props: {
    training: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      aiForm: {
        target_audience: '',
        training_goal: '',
        prompt: '',
      },
      aiOptions: {
        language: 'fr',
        difficulty_level: 'intermediate',
        estimated_duration: 'medium',
        hasSections: true,
        hasTasks: !!this.isNotPercentPlan,
        hasQuizzes: true,
        hasAssignments: false,
      },
      durationCreditCosts: {
        short: 1,
        medium: 2,
        long: 3,
        very_long: 4,
        extensive: 5,
        comprehensive: 8,
      },
      checked: true,
    };
  },
  validations: {
    aiForm: {
      target_audience: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(500),
      },
      training_goal: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(500),
      },
      prompt: {
        required,
        minLength: minLength(10),
        maxLength: maxLength(2000),
      },
    },
  },
  computed: {
    isNotPercentPlan() {
      return this.$store.getters['auth/isNotPercentPlan'];
    },
    availableAiCredits() {
      return this.$store.getters['auth/aiCreditsLeft'] || 0;
    },
    aiCreditsRequired() {
      return this.durationCreditCosts[this.aiOptions.estimated_duration] || 2;
    },
    hasEnoughCredits() {
      return this.availableAiCredits >= this.aiCreditsRequired;
    },
    hasUnsavedWork() {
      return this.aiForm.prompt.trim().length > 0
        || this.aiForm.target_audience.trim().length > 0
        || this.aiForm.training_goal.trim().length > 0;
    },
  },
  methods: {
    confirmClose() {
      if (this.hasUnsavedWork) {
        this.$buefy.dialog.confirm({
          title: 'Confirmation',
          message: 'Vous avez des informations non sauvegardées. Voulez-vous vraiment fermer ?',
          confirmText: 'Oui, fermer',
          cancelText: 'Annuler',
          type: 'is-warning',
          onConfirm: () => {
            this.$parent.close();
          },
        });
      } else {
        this.$parent.close();
      }
    },
    async generateWithAI() {
      let message;

      if (this.$store.getters['auth/hasEnterpriseCapabilities']) {
        message = 'Vous avez atteint les limites de cette fonctionnalité, veuillez vous rapprocher de votre chargée de compte pour augmenter ces limites.';
      } else {
        message = `Vous n'avez pas assez de crédits IA (${this.aiCreditsRequired} requis)`;
      }

      if (!this.hasEnoughCredits) {
        this.$buefy.toast.open({
          message,
          type: 'is-danger',
        });
        return;
      }

      this.isLoading = true;

      try {
        const filteredOptions = {
          estimated_duration: this.aiOptions.estimated_duration,
        };
        if (this.aiOptions.language !== 'custom') {
          filteredOptions.language = this.aiOptions.language;
        }
        if (this.aiOptions.difficulty_level !== 'custom') {
          filteredOptions.difficulty_level = this.aiOptions.difficulty_level;
        }

        const response = await this.$store.dispatch('trainings/createAIItemStructure', {
          uuid: this.training.uuid,
          options: {
            prompt: this.aiForm.prompt,
            target_audience: this.aiForm.target_audience,
            training_goal: this.aiForm.training_goal,
            has_sections: this.aiOptions.hasSections,
            has_tasks: this.aiOptions.hasTasks,
            has_quizzes: this.aiOptions.hasQuizzes,
            has_assignments: this.aiOptions.hasAssignments,
            ...filteredOptions,
          },
        });

        // Convert the AI-generated structure to component format with temporary IDs
        const generatedItems = response.structure.map((item, index) => ({
          id: Date.now() + index,
          name: item.name,
          type: item.type,
          children: item.children ? item.children.map((child, childIndex) => ({
            id: Date.now() + index + childIndex + 1000,
            name: child.name,
            type: child.type,
          })) : [],
        }));

        this.$buefy.toast.open({
          message: 'Structure générée avec succès ! Vous pouvez maintenant la modifier si nécessaire.',
          type: 'is-success',
          duration: 4000,
        });

        await this.$store.dispatch('auth/fetch');

        // Emit the generated structure and close this modal
        this.$emit('structure-generated', generatedItems);
        this.$parent.close();
      } catch (error) {
        console.error('Error generating AI structure:', error);
        this.$buefy.toast.open({
          message: (error.response && error.response.data && error.response.data.message) || 'Erreur lors de la génération de la structure',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="modal-card" style="width: 800px;">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <b-icon icon="magic" class="mr-2" />
        Génération assistée par IA
      </p>
    </header>

    <section class="modal-card-body">
      <div class="content">
        <p class="help mb-4">
          Décrivez votre formation : l'IA vous aide à concevoir une structure pédagogique claire et cohérente,
          que vous pourrez ajuster librement.
        </p>

        <form @submit.prevent="dataIsValid(isLoading, $v.aiForm) && generateWithAI()">
          <b-field label="Public cible *" v-bind="$getErrorProps($v.aiForm.target_audience, ['required', { name: 'minLength', args: [5] }, { name: 'maxLength', args: [500] }])">
            <b-input v-model="aiForm.target_audience"
              placeholder="Ex: Entrepreneurs débutants, étudiants en marketing, professionnels souhaitant se reconvertir..."
              :loading="isLoading"
              maxlength="500" />
          </b-field>

          <b-field label="Objectif de la formation *" v-bind="$getErrorProps($v.aiForm.training_goal, ['required', { name: 'minLength', args: [5] }, { name: 'maxLength', args: [500] }])">
            <b-input v-model="aiForm.training_goal"
              placeholder="Ex: Maîtriser les bases du marketing digital, créer une stratégie complète, obtenir plus de clients..."
              :loading="isLoading"
              maxlength="500" />
          </b-field>

          <div class="columns">
            <div class="column">
              <b-field label="Langue">
                <b-select v-model="aiOptions.language" expanded>
                  <option value="fr">Français</option>
                  <option value="en">Anglais</option>
                  <option value="custom">Je préciserai moi-même à l'IA</option>
                </b-select>
              </b-field>
            </div>

            <div class="column">
              <b-field label="Niveau de difficulté">
                <b-select v-model="aiOptions.difficulty_level" expanded>
                  <option value="beginner">Débutant</option>
                  <option value="intermediate">Intermédiaire</option>
                  <option value="advanced">Avancé</option>
                  <option value="custom">Je préciserai moi-même à l'IA</option>
                </b-select>
              </b-field>
            </div>

            <div class="column">
              <b-field label="Durée estimée">
                <b-select v-model="aiOptions.estimated_duration" expanded>
                  <option value="short">
                    Courte (5-10 leçons)
                    <template v-if="!$store.getters['auth/hasEnterpriseCapabilities']">
                      - 1 crédit
                    </template>
                  </option>
                  <option value="medium">
                    Moyenne (10-20 leçons)
                    <template v-if="!$store.getters['auth/hasEnterpriseCapabilities']">
                      - 2 crédits
                    </template>
                  </option>
                  <option value="long">
                    Longue (20-50 leçons)
                    <template v-if="!$store.getters['auth/hasEnterpriseCapabilities']">
                      - 3 crédits
                    </template>
                  </option>
                  <option value="very_long">
                    Très longue (50-80 leçons)
                    <template v-if="!$store.getters['auth/hasEnterpriseCapabilities']">
                      - 4 crédits
                    </template>
                  </option>
                  <option value="extensive">
                    Extensive (80-100 leçons)
                    <template v-if="!$store.getters['auth/hasEnterpriseCapabilities']">
                      - 5 crédits
                    </template>
                  </option>
                  <option value="comprehensive">
                    Complète (100+ leçons)
                    <template v-if="!$store.getters['auth/hasEnterpriseCapabilities']">
                      - 8 crédits
                    </template>
                  </option>
                </b-select>
              </b-field>
            </div>
          </div>

          <b-field class="mb-5" label="Éléments à inclure dans la structure">
            <b-checkbox v-model="checked" disabled>
              Leçons
            </b-checkbox>
            <b-checkbox v-model="aiOptions.hasSections">
              Chapitres
            </b-checkbox>
            <b-checkbox v-model="aiOptions.hasQuizzes">
              Quiz multimédia
            </b-checkbox>
            <b-checkbox v-model="aiOptions.hasTasks" :disabled="!isNotPercentPlan">
              Tâches & missions
            </b-checkbox>
            <b-checkbox v-model="aiOptions.hasAssignments" :disabled="!isNotPercentPlan">
              Devoirs
            </b-checkbox>
          </b-field>

          <b-field label="Description de votre formation *" v-bind="$getErrorProps($v.aiForm.prompt, ['required', { name: 'minLength', args: [10] }, { name: 'maxLength', args: [2000] }])">
            <b-input
              v-model="aiForm.prompt"
              type="textarea"
              placeholder="Présentez le contenu, les grandes étapes et les compétences travaillées.
Plus la description est précise, plus la structure proposée sera pertinente."
              :loading="isLoading"
              maxlength="2000" />
          </b-field>

          <div v-if="!$store.getters['auth/hasEnterpriseCapabilities']" class="notification is-info is-light">
            <div class="is-flex is-justify-content-space-between is-align-items-center">
              <div>
                <strong>Coût :</strong> {{ aiCreditsRequired }} crédits IA
              </div>
              <div>
                <strong>Disponibles :</strong> {{ availableAiCredits }} crédits
              </div>
            </div>
          </div>

          <p class="help has-text-centered" style="font-size:12px">
            Cette fonctionnalité utilise une IA qui peut, comme toute IA, faire des erreurs. Veillez donc à vérifier les contenus générés.<br/>
            La structure générée est entièrement modifiable.
          </p>
        </form>
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end btop-grey">
      <b-button type="is-text" @click="confirmClose">
        Annuler
      </b-button>
      <b-button type="is-primary" icon-left="magic" :loading="isLoading"
        :disabled="!hasEnoughCredits"
        @click="dataIsValid(isLoading, $v.aiForm) && generateWithAI()">
        Générer votre structure
      </b-button>
    </footer>
  </div>
</template>
